import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import { NoFilters } from "./Filters/NoFilters";
import { Panel, PanelHeader, PanelMobile } from "../Layout/Panel";
import { SortedList, SortedListPagination } from "../Layout/SortedList";
import { filterCustomerList } from "./Filters/FilterHelpers";
import { getCustomerSelectionsSelector, getCustomersSelector, getIsLoadingSelector } from "../../selectors/selectors";
import { getCustomers, setSessionPreferences, updateUserMetadata } from "../../actions";
import { searchColumns, sortColumns } from "./List/SearchColumns";
import { withCustomerFilters } from "../Common/Preferences/withCustomerFilters";
import { withNavigationHistory } from "../Common/Navigation/withNavigationHistory";
import FiltersRow from "./Filters/FiltersRow";
import FiltersSidePanel from "./Filters/FiltersSidePanel";
import ListHeader from "./List/ListHeader";
import ListRow from "./List/ListRow";
import PanelTools from "./../Layout/Panel/PanelTools";

class ListPage extends PureComponent {
  static propTypes = {
    getCustomers: PropTypes.func.isRequired,
    setSessionPreferences: PropTypes.func.isRequired,
    updateUserMetadata: PropTypes.func.isRequired,
    customers: PropTypes.array,
    customersFilters: PropTypes.object,
    customersSelections: PropTypes.array,
    isLoading: PropTypes.bool,
    isMobile: PropTypes.bool,
    user: PropTypes.object,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    customers: [],
    customersFilters: null,
    customersSelections: [],
    isLoading: false,
    isMobile: false,
    user: {},
    userMetadata: {}
  };

  state = {
    isFiltersSidePanelOpen: false,
    viewState: {
      currentPage: 1,
      searchQuery: "",
      sort: {}
    }
  };

  componentDidMount() {
    this.props.getCustomers();
  }

  handleDeleteCurrentFilter = value => {
    const newCustomerFilters = { ...this.props.customersFilters };

    Object.keys(newCustomerFilters).forEach(filterName => {
      const filter = newCustomerFilters[filterName];
      if (Array.isArray(filter)) {
        newCustomerFilters[filterName] = filter.filter(filterValue => value !== filterValue);
      } else if (filter === value) {
        newCustomerFilters[filterName] = "";
      }
    });

    this.props.setSessionPreferences({ customersFilters: newCustomerFilters });
  };

  handleSetViewState = item => this.setState({ viewState: { ...this.state.viewState, ...item } });

  handleChangePagination = currentPage => this.handleSetViewState({ currentPage });

  handleChangeSort = sort => this.handleSetViewState({ sort });

  handleSearch = searchQuery => this.handleSetViewState({ searchQuery, currentPage: 1 });

  handleSetSessionCustomersFilters = customersFilters => {
    this.props.setSessionPreferences({ customersFilters: { ...customersFilters } });
  };

  handleSetFilters = customersFilters => {
    this.props.setSessionPreferences({ customersFilters });
    this.props.updateUserMetadata({ ...this.props.userMetadata, customersFilters }, this.props.user.sub);
  };

  handleToggleFiltersPanel = () => this.setState({ isFiltersSidePanelOpen: !this.state.isFiltersSidePanelOpen });

  handleResetFilters = () => this.handleSetSessionCustomersFilters(this.props.userMetadata.customersFilters);

  handleClearFilters = () => this.handleSetSessionCustomersFilters({});

  actions = [
    {
      label: i18n.t("customer.filters"),
      onClick: this.handleToggleFiltersPanel,
      className: "--right"
    }
  ];

  render() {
    return (
      <Fragment>
        {this.props.isMobile ? (
          <PanelMobile className="list-page">
            <PanelHeader id={i18n.t("customer.customers")} actions={this.actions} isMobile={this.props.isMobile} isFirstPage />

            {!this.props.customersFilters && <NoFilters onClick={this.handleToggleFiltersPanel} />}

            {this.props.customersFilters && (
              <Fragment>
                <PanelTools
                  searchQuery={this.state.viewState.searchQuery}
                  isUnitsOfMeasureVisible={false}
                  onSearch={this.handleSearch}
                  isSearchVisible
                />
                <FiltersRow
                  customersSelections={this.props.customersSelections}
                  filters={this.props.customersFilters}
                  onClearFilters={this.handleClearFilters}
                  onDeleteFilter={this.handleDeleteCurrentFilter}
                  onResetFilters={this.handleResetFilters}
                />
                <SortedList
                  RowComponent={ListRow}
                  PaginationComponent={SortedListPagination}
                  HeaderComponent={ListHeader}
                  currentPage={this.state.viewState.currentPage}
                  data={filterCustomerList(this.props.customers, this.props.customersFilters)}
                  isLoading={this.props.isLoading}
                  isMobile={this.props.isMobile}
                  noResultMessage={i18n.t("table.noSearchResultsMessage")}
                  noResultTitle={i18n.t("table.noSearchResultsTitle")}
                  onChangePagination={this.handleChangePagination}
                  onChangeSort={this.handleChangeSort}
                  searchQuery={this.state.viewState.searchQuery}
                  searchValues={searchColumns}
                  sortValues={sortColumns}
                  sort={this.state.viewState.sort}
                />
              </Fragment>
            )}

            <FiltersSidePanel
              customersSelections={this.props.customersSelections}
              customersFilters={this.props.customersFilters}
              position="right"
              isMobile={this.props.isMobile}
              isVisible={this.state.isFiltersSidePanelOpen}
              panelVisibility={this.handleToggleFiltersPanel}
              onSetFilters={this.handleSetFilters}
              onSearch={this.handleSetSessionCustomersFilters}
              updateUserMetadata={this.props.updateUserMetadata}
              userMetadata={this.props.userMetadata}
            />
          </PanelMobile>
        ) : (
          <Panel className="list-page">
            <PanelHeader id={i18n.t("customer.customers")} actions={this.actions} isMobile={this.props.isMobile} isFirstPage />

            {!this.props.customersFilters && <NoFilters onClick={this.handleToggleFiltersPanel} />}

            {this.props.customersFilters && (
              <Fragment>
                <PanelTools
                  searchQuery={this.state.viewState.searchQuery}
                  isUnitsOfMeasureVisible={false}
                  onSearch={this.handleSearch}
                  isSearchVisible
                />
                <FiltersRow
                  customersSelections={this.props.customersSelections}
                  filters={this.props.customersFilters}
                  onClearFilters={this.handleClearFilters}
                  onDeleteFilter={this.handleDeleteCurrentFilter}
                  onResetFilters={this.handleResetFilters}
                />
                <SortedList
                  RowComponent={ListRow}
                  PaginationComponent={SortedListPagination}
                  HeaderComponent={ListHeader}
                  currentPage={this.state.viewState.currentPage}
                  data={filterCustomerList(this.props.customers, this.props.customersFilters)}
                  isLoading={this.props.isLoading}
                  isMobile={this.props.isMobile}
                  noResultMessage={i18n.t("table.noSearchResultsMessage")}
                  noResultTitle={i18n.t("table.noSearchResultsTitle")}
                  onChangePagination={this.handleChangePagination}
                  onChangeSort={this.handleChangeSort}
                  searchQuery={this.state.viewState.searchQuery}
                  searchValues={searchColumns}
                  sortValues={sortColumns}
                  sort={this.state.viewState.sort}
                />
              </Fragment>
            )}

            <FiltersSidePanel
              customersSelections={this.props.customersSelections}
              customersFilters={this.props.customersFilters}
              position="right"
              isMobile={this.props.isMobile}
              isVisible={this.state.isFiltersSidePanelOpen}
              panelVisibility={this.handleToggleFiltersPanel}
              onSetFilters={this.handleSetFilters}
              onSearch={this.handleSetSessionCustomersFilters}
              updateUserMetadata={this.props.updateUserMetadata}
              userMetadata={this.props.userMetadata}
            />
          </Panel>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  return {
    customersSelections: getCustomerSelectionsSelector(state),
    customers: getCustomersSelector(state),
    isLoading: getIsLoadingSelector(state, [getCustomers.id]),
    isMobile: state.app.isMobile,
    userMetadata: state.app.userMetadata,
    user: state.app.user
  };
};

const component = connect(
  mapStateToProps,
  { getCustomers, setSessionPreferences, updateUserMetadata }
)(withCustomerFilters(withNavigationHistory(ListPage)));

export default component;



// WEBPACK FOOTER //
// ./src/components/Customer/ListPage.js